import type { ColumnVisibilityState, RowSelectionState } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import { Building2, Columns3, Plus, RotateCw, Search, Upload, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  MultiSelect,
} from "~/components/ui";
import type { UserRow } from "~/features/admin/users/types";
import { BulkUserOrganizationChangeDialog } from "./BulkUserOrganizationChangeDialog";
import { UserFormDialog } from "./UserFormDialog";
import { UserOrganizationChangeDialog } from "./UserOrganizationChangeDialog";
import { UserPeek } from "./UserPeek";
import { UsersTable } from "./UsersTable";

interface FilterOption {
  value: string;
  label: string;
}

const USER_COLUMNS = [
  { id: "displayName", labelKey: "model.user.displayName" },
  { id: "email", labelKey: "model.user.email" },
  { id: "organization", labelKey: "model.user.organization" },
  { id: "segments", labelKey: "model.user.segments" },
  { id: "groups", labelKey: "model.user.groups" },
  { id: "memo", labelKey: "model.user.memo" },
] as const;

interface UsersPresenterProps {
  rows: UserRow[];
  segmentOptions: FilterOption[];
  groupOptions: FilterOption[];
  organizationOptions: FilterOption[];
  productRoleOptions: FilterOption[];
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  onPageReset: () => void;
  onRefresh: () => void;
  selectedUser: string | null;
  onSelect: (id: string) => void;
  onCloseSelected: () => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (next: RowSelectionState) => void;
  onBulkOrganizationChangeCompleted: () => void;
}

export function UsersPresenter({
  rows,
  segmentOptions,
  groupOptions,
  organizationOptions,
  productRoleOptions,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  onPageReset,
  onRefresh,
  selectedUser,
  onSelect,
  onCloseSelected,
  rowSelection,
  onRowSelectionChange,
  onBulkOrganizationChangeCompleted,
}: UsersPresenterProps) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [excludeDeactivated, setExcludeDeactivated] = useState(true);
  const [search, setSearch] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedProductRoles, setSelectedProductRoles] = useState<string[]>([]);
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [organizationChangeTarget, setOrganizationChangeTarget] = useState<UserRow | null>(null);
  const [bulkOrganizationChangeOpen, setBulkOrganizationChangeOpen] = useState(false);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (excludeDeactivated && row.isDeactivated) return false;
        if (selectedOrganizations.length > 0 && !selectedOrganizations.includes(row.organization.shortCode)) {
          return false;
        }
        if (
          selectedProductRoles.length > 0 &&
          !row.productRoles.some((productRole) =>
            selectedProductRoles.includes(`${productRole.productName}:${productRole.roleName}`),
          )
        ) {
          return false;
        }
        if (selectedSegments.length > 0 && !row.segments.some((s) => selectedSegments.includes(s))) return false;
        if (selectedGroups.length > 0 && !row.groups.some((g) => selectedGroups.includes(g))) return false;
        const createdDate = format(parseISO(row.createdAt), "yyyy-MM-dd");
        if (createdFrom && createdDate < createdFrom) return false;
        if (createdTo && createdDate > createdTo) return false;
        return true;
      }),
    [
      rows,
      excludeDeactivated,
      selectedOrganizations,
      selectedProductRoles,
      selectedSegments,
      selectedGroups,
      createdFrom,
      createdTo,
    ],
  );
  const selectedRows = useMemo(() => rows.filter((row) => rowSelection[row.id]), [rows, rowSelection]);

  const onExcludeDeactivatedChange = (next: boolean) => {
    setExcludeDeactivated(next);
    onPageReset();
  };
  const onSearchChange = (next: string) => {
    setSearch(next);
    onPageReset();
  };
  const onSegmentsChange = (next: string[]) => {
    setSelectedSegments(next);
    onPageReset();
  };
  const onGroupsChange = (next: string[]) => {
    setSelectedGroups(next);
    onPageReset();
  };
  const onOrganizationsChange = (next: string[]) => {
    setSelectedOrganizations(next);
    onPageReset();
  };
  const onProductRolesChange = (next: string[]) => {
    setSelectedProductRoles(next);
    onPageReset();
  };
  const onCreatedFromChange = (next: string) => {
    setCreatedFrom(next);
    onPageReset();
  };
  const onCreatedToChange = (next: string) => {
    setCreatedTo(next);
    onPageReset();
  };
  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) => setColumnVisibility(Object.fromEntries(USER_COLUMNS.map((c) => [c.id, next])));

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-dewey-title capitalize">{t("model.user.modelNames")}</h1>
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-3" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("ui.search")}
              className="h-9 w-[240px] pl-8"
              aria-label={t("ui.search")}
            />
          </div>
          <MultiSelect
            label={t("model.user.organization")}
            options={organizationOptions}
            selected={selectedOrganizations}
            onChange={onOrganizationsChange}
          />
          <MultiSelect
            label={t("feature.users.filters.productRole")}
            options={productRoleOptions}
            selected={selectedProductRoles}
            onChange={onProductRolesChange}
          />
          <MultiSelect
            label={t("model.user.segments")}
            options={segmentOptions}
            selected={selectedSegments}
            onChange={onSegmentsChange}
          />
          <MultiSelect
            label={t("model.user.groups")}
            options={groupOptions}
            selected={selectedGroups}
            onChange={onGroupsChange}
          />
          <Input
            type="date"
            value={createdFrom}
            onChange={(event) => onCreatedFromChange(event.target.value)}
            className="h-9 w-[150px]"
            aria-label={t("feature.users.filters.createdFrom")}
          />
          <Input
            type="date"
            value={createdTo}
            onChange={(event) => onCreatedToChange(event.target.value)}
            className="h-9 w-[150px]"
            aria-label={t("feature.users.filters.createdTo")}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
                <Columns3 />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {USER_COLUMNS.map((column) => (
                <DropdownMenuCheckboxItem
                  key={column.id}
                  checked={isVisible(column.id)}
                  onCheckedChange={(checked) => setVisibility(column.id, checked === true)}
                  onSelect={(event) => event.preventDefault()}
                >
                  {t(column.labelKey)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label={t("ui.reload")}>
            <RotateCw />
          </Button>
          <label
            htmlFor="users-exclude-deactivated"
            className="inline-flex items-center gap-2 text-14-regular text-fg-2"
          >
            <Checkbox
              id="users-exclude-deactivated"
              checked={excludeDeactivated}
              onCheckedChange={(v) => onExcludeDeactivatedChange(v === true)}
            />
            <span>{t("ui.excludeDeactivated")}</span>
          </label>
          <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus />
            <span>{t("ui.add")}</span>
          </Button>
          <Button type="button" variant="outline" size="sm" asChild>
            <Link to="bulk-upload">
              <Upload />
              <span>{t("model.users.bulkCreate.bulkUpload")}</span>
            </Link>
          </Button>
        </div>
      </div>

      <UserFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      {organizationChangeTarget && (
        <UserOrganizationChangeDialog
          key={organizationChangeTarget.id}
          user={organizationChangeTarget}
          onOpenChange={(open) => !open && setOrganizationChangeTarget(null)}
        />
      )}

      {selectedRows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-2.5">
          <div className="text-14-regular tabular-nums text-fg-1">
            {t("feature.users.bulkOrganizationChange.selectedCount", { count: selectedRows.length })}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => onRowSelectionChange({})}>
              <X />
              <span>{t("feature.users.bulkOrganizationChange.clearSelection")}</span>
            </Button>
            <Button type="button" size="sm" onClick={() => setBulkOrganizationChangeOpen(true)}>
              <Building2 />
              <span>{t("feature.users.bulkOrganizationChange.title")}</span>
            </Button>
          </div>
        </div>
      )}

      <UsersTable
        rows={filteredRows}
        onSelect={onSelect}
        onChangeOrganization={setOrganizationChangeTarget}
        emptyMessage={t("ui.notFound")}
        globalFilter={search}
        onGlobalFilterChange={onSearchChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        pageIndex={pageIndex}
        onPageIndexChange={onPageIndexChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
      />

      <UserPeek id={selectedUser} onClose={onCloseSelected} />
      <BulkUserOrganizationChangeDialog
        open={bulkOrganizationChangeOpen}
        onClose={() => setBulkOrganizationChangeOpen(false)}
        users={selectedRows}
        onCompleted={onBulkOrganizationChangeCompleted}
      />
    </div>
  );
}
